/**
 * O cartão "Rever leitura na fonte" (E155.F3, GUS-192, ADR-0105 §2.7), aberto na linha da ficha:
 * o campo como o exame o usa, a releitura pelo papel ("Solicitar nova leitura deste ponto") e a
 * confirmação com fonte (o valor como está impresso, o documento e a página), sempre com motivo.
 * A leitura proposta volta com a fonte; a ambígua lista as candidatas; a não localizada abre a
 * confirmação. O que mudou já entrou no exame e foi verificado de novo; a entrega anterior é
 * preservada. A revisão precisa de fonte e autoria: um pedido do cliente não substitui a técnica.
 */
import type { AxiosInstance } from 'axios';
import { useId, useState } from 'react';

import { Button, Field, Input, Select } from '@/components/ui';
import type { CampoDaFichaData, DocumentoDaFichaData } from '@/lib/ficha_da_leitura';
import {
  ReleituraRecusadaError,
  motivoCabe,
  pedirReleitura,
  type CandidataDaReleituraData,
  type PedidoDeReleituraData,
  type RecusaDaReleitura,
  type ReleituraOutData,
  type ResultadoDaReleitura,
} from '@/lib/releitura_da_leitura';

import {
  AvisoDaReleitura,
  CampoAtual,
  LeituraProposta,
  MotivoDaReleitura,
  Recusa,
  type VerNaFonte,
} from './ReverLeituraPartes';
import { r } from './textos';

interface Correcao {
  valor: string;
  documentoId: string;
  pagina: string;
}

const VALOR_MAXIMO = 512;
const CONCLUIDOS: readonly ResultadoDaReleitura[] = ['mudou', 'sem_mudanca'];

type Extra = Pick<PedidoDeReleituraData, 'valor' | 'documento_id' | 'pagina'>;

function useReleitura(
  client: AxiosInstance,
  caseId: string,
  campo: CampoDaFichaData,
  documentos: DocumentoDaFichaData[],
  onFeito: (resultado: ResultadoDaReleitura) => void,
) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [recusa, setRecusa] = useState<RecusaDaReleitura | null>(null);
  const [resposta, setResposta] = useState<ReleituraOutData | null>(null);
  const [correcao, setCorrecao] = useState<Correcao | null>(null);
  const primeiroDocumento = documentos[0]?.documento_id ?? '';

  async function enviar(extra: Extra = {}): Promise<void> {
    setEnviando(true);
    setRecusa(null);
    try {
      const dados = await pedirReleitura(client, caseId, {
        campo: campo.campo,
        referente_id: campo.referente_id,
        motivo: motivo.trim(),
        ...extra,
      });
      setResposta(dados);
      const resultado = dados.releitura.resultado;
      setCorrecao(
        resultado === 'nao_localizado'
          ? { valor: '', documentoId: primeiroDocumento, pagina: '' }
          : null,
      );
      if (CONCLUIDOS.includes(resultado)) onFeito(resultado);
    } catch (err) {
      setRecusa(err instanceof ReleituraRecusadaError ? err.recusa : 'indisponivel');
    } finally {
      setEnviando(false);
    }
  }

  function abrirCorrecao(): void {
    setCorrecao({ valor: '', documentoId: primeiroDocumento, pagina: '' });
  }

  function usarCandidata(candidata: CandidataDaReleituraData): void {
    const pagina = candidata.fonte.pagina;
    setCorrecao({
      valor: candidata.literal ?? candidata.valor,
      documentoId: candidata.fonte.documento_id,
      pagina: pagina === null ? '' : String(pagina),
    });
  }

  function confirmar(): void {
    if (correcao === null) return;
    void enviar({
      valor: correcao.valor.trim(),
      documento_id: correcao.documentoId,
      pagina: correcao.pagina === '' ? null : Number(correcao.pagina),
    });
  }

  const cabe = motivoCabe(motivo);
  const feita = resposta !== null && CONCLUIDOS.includes(resposta.releitura.resultado);
  const podeConfirmar =
    cabe && correcao !== null && correcao.valor.trim() !== '' && correcao.documentoId !== '';
  return {
    motivo,
    setMotivo,
    enviando,
    recusa,
    resposta,
    correcao,
    setCorrecao,
    solicitar: () => void enviar(),
    abrirCorrecao,
    usarCandidata,
    confirmar,
    cabe,
    feita,
    podeConfirmar,
  };
}

type Uso = ReturnType<typeof useReleitura>;

function FormularioDaCorrecao({
  uso,
  documentos,
}: {
  uso: Uso;
  documentos: DocumentoDaFichaData[];
}): JSX.Element | null {
  const { correcao, setCorrecao } = uso;
  if (correcao === null) return null;
  return (
    <form
      className="space-y-sc-2 border border-border p-sc-3"
      data-testid="releitura-correcao"
      onSubmit={(evento) => {
        evento.preventDefault();
        uso.confirmar();
      }}
    >
      <Field label={r.valorRotulo} required>
        <Input
          value={correcao.valor}
          onChange={(evento) => setCorrecao({ ...correcao, valor: evento.target.value })}
          maxLength={VALOR_MAXIMO}
          data-testid="releitura-valor-input"
        />
      </Field>
      <div className="grid gap-sc-2 sm:grid-cols-2">
        <Field label={r.documentoRotulo} required>
          <Select
            value={correcao.documentoId}
            onChange={(evento) => setCorrecao({ ...correcao, documentoId: evento.target.value })}
            data-testid="releitura-documento"
          >
            {documentos.map((documento) => (
              <option key={documento.documento_id} value={documento.documento_id}>
                {documento.documento_nome}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={r.paginaRotulo} description={r.paginaAjuda}>
          <Input
            type="number"
            min={1}
            value={correcao.pagina}
            onChange={(evento) => setCorrecao({ ...correcao, pagina: evento.target.value })}
            data-testid="releitura-pagina"
          />
        </Field>
      </div>
      <p className="text-body-sm text-fg-muted">{r.autoria}</p>
      <Button
        type="submit"
        size="sm"
        disabled={uso.enviando || !uso.podeConfirmar}
        data-testid="releitura-confirmar"
      >
        {uso.enviando ? r.enviando : r.confirmar}
      </Button>
    </form>
  );
}

function Acoes({ uso, onFechar }: { uso: Uso; onFechar: () => void }): JSX.Element {
  return (
    <div className="flex flex-wrap gap-sc-2">
      <Button
        type="button"
        size="sm"
        disabled={uso.enviando || !uso.cabe}
        onClick={uso.solicitar}
        data-testid="releitura-solicitar"
      >
        {uso.enviando ? r.enviando : r.solicitar}
      </Button>
      {uso.correcao === null ? (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={uso.enviando}
          onClick={uso.abrirCorrecao}
          data-testid="releitura-abrir-correcao"
        >
          {r.confirmarComFonte}
        </Button>
      ) : null}
      <Button type="button" size="sm" variant="ghost" onClick={onFechar}>
        {r.voltar}
      </Button>
    </div>
  );
}

export interface ReverLeituraProps {
  client: AxiosInstance;
  caseId: string;
  campo: CampoDaFichaData;
  documentos: DocumentoDaFichaData[];
  /** A versão da ficha em que o campo está. */
  versao: number;
  onVerNaFonte: VerNaFonte;
  /** A releitura registrada: a ficha relê; o que mudou avisa a página. */
  onFeito: (resultado: ResultadoDaReleitura) => void;
  onFechar: () => void;
}

export function ReverLeitura({
  client,
  caseId,
  campo,
  documentos,
  versao,
  onVerNaFonte,
  onFeito,
  onFechar,
}: ReverLeituraProps): JSX.Element {
  const uso = useReleitura(client, caseId, campo, documentos, onFeito);
  const idDoTitulo = useId();
  return (
    <section
      aria-labelledby={idDoTitulo}
      className="space-y-sc-3 border border-accent-200 bg-accent-bg p-sc-4"
      data-testid="releitura-cartao"
      data-campo={campo.campo}
      data-referente={campo.referente_id ?? ''}
    >
      <div>
        <h4 id={idDoTitulo} className="text-body-md font-semibold text-fg">
          {r.titulo}
        </h4>
        <p className="text-body-sm text-fg-muted">{r.subtitulo}</p>
      </div>
      <p className="text-caption font-semibold text-fg-dim">{r.secaoCampo}</p>
      <CampoAtual campo={campo} versao={versao} onVerNaFonte={onVerNaFonte} />
      {uso.resposta !== null ? (
        <LeituraProposta
          resposta={uso.resposta}
          onVerNaFonte={onVerNaFonte}
          onUsarCandidata={uso.usarCandidata}
        />
      ) : null}
      {uso.feita ? (
        <>
          <AvisoDaReleitura />
          <Button type="button" size="sm" variant="ghost" onClick={onFechar}>
            {r.voltar}
          </Button>
        </>
      ) : (
        <>
          <MotivoDaReleitura
            motivo={uso.motivo}
            onChange={uso.setMotivo}
            testId="releitura-motivo"
          />
          <FormularioDaCorrecao uso={uso} documentos={documentos} />
          <Acoes uso={uso} onFechar={onFechar} />
        </>
      )}
      <Recusa recusa={uso.recusa} />
      <p className="text-caption text-fg-muted" data-testid="releitura-rodape">
        {r.rodape}
      </p>
    </section>
  );
}
